<script lang="ts">
    import {onMount} from 'svelte';
    import {Loader} from 'google-maps';
    import {MarkerClusterer, GridAlgorithm} from "@googlemaps/markerclusterer";
    import QueryString from "qs";
    import * as cluster from "cluster";
    import * as Cluster from "cluster";

    // define types
    type location = {
        lat: number,
	    lng: number,
        postalCode: string,
	    quantity: number
    }

    type locationDetail = {
        lat: number,
        lng: number,
        postalCode: string,
	    date: string,
        attempt: boolean
    }

    // all used variables
    const urlParams = new URLSearchParams(window.location.search);
    export let defaultMapCenter;
    export let defaultMapZoom;
    let view : string = 'map';
    let zoom: number = 14;
    let postalCode: string = urlParams.get('pc') || '';
		let isStandardPc = false;
    let distance: string = '+5km';
	let email: string = '';
    let burglary: boolean = true;
    let burglaryAttempt: boolean = true;
    let isLoading: boolean = false;
    let showSearchBox: boolean = true;
	let locations: locationDetail[]= [];
    let allLocations: location[]= [];
    let infoWindowLocations: locationDetail[]= [];
    let infoWindowPostalCode: string = '';
    let infoWindowLoading = false;
    let noResultsMessage = false;
    let notFoundMessage = false;
    let noEmailMessage = false;
	var burglaryFormExists = false;
	var burglaryForm;
	var burglaryFormEmail;
	var burglaryFormSubmit;
    let map: google.maps.Map;
    let mapsDiv: HTMLDivElement;
    let markers: google.maps.Marker[] = [];
    let markerCluster: MarkerClusterer;
    let mapInfoWindow;

		if (typeof standardPc != 'undefined') {
			postalCode = standardPc;
			isStandardPc = true;
		}

    // load google maps
    let google;
    const loader = new Loader('AIzaSyD4F8j9j8W8da5Swuzre2pR4XZG2ZhffsU');
    onMount(async () => {
        google = await loader.load();
        map = new google.maps.Map(mapsDiv, {
            center: defaultMapCenter,
            zoom: defaultMapZoom,
            streetViewControl: false,
            mapTypeId: google.maps.MapTypeId.ROADMAP,
            mapTypeControl: false,
        });

        // create info window
        mapInfoWindow = new google.maps.InfoWindow({content: document.getElementById('infoWindowContent')});

        updateAllLocations();
        search();
    });

    $: {
        // react on changes of distance variables:
        distance

		switch(distance) {
            case '+1km': zoom = 14; break;
            case '+2km': zoom = 13; break;
            case '+5km': zoom = 12; break;
            case '+10km': zoom = 11; break;
            case '+25km': zoom = 10; break;
		}
    }

    // search
    async function updateAllLocations() : Promise<void> {
        isLoading = true;
        const res = await fetch('/wp-json/burglary-check/v1/search?'+
            QueryString.stringify({
                'burglary': burglary?1:0,
                'burglaryAttempt': burglaryAttempt?1:0}), {
            method: 'GET'
        });

        const json = await res.json();
        if (json.status === 'ok')
            allLocations = json.locations;
        isLoading = false;
    }

	function validateEmail(email) {
		if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w+)+$/.test(email)) {
			return (true)
		}
		return (false)
	}

	if (document.getElementById('burglary-check-form') && !document.body.classList.contains('fl-builder-edit')) {
		burglaryFormExists = true;
		document.getElementById('burglary-check-form').style.width = '0';
		document.getElementById('burglary-check-form').style.height = '0';
	}

	async function search() : Promise<void> {
        if (postalCode.length > 0) {
			noResultsMessage = false;
			notFoundMessage = false;
			noEmailMessage = false;
			if (validateEmail(email) === false && burglaryFormExists) {
				noEmailMessage = true;
			} else {
				if (burglaryFormExists) {
					burglaryForm = document.getElementById('burglary-check-form');
					burglaryForm = burglaryForm.querySelector('iframe');
					burglaryFormEmail = burglaryForm.contentWindow.document.querySelector('input[type="email"]');
					burglaryFormEmail.value = email;
					burglaryFormSubmit = burglaryForm.contentWindow.document.querySelector('input[type="submit"]');
				}

				if (isStandardPc) {
					document.getElementById('burglary-info').style.display = 'none';
					document.getElementById('burglary-map').style.flexBasis = '100%';
				}
				updateAllLocations();

				// reset to map view
				view = 'map';
				isLoading = true;
				const res = await fetch('/wp-json/burglary-check/v1/details?' +
						QueryString.stringify({
							'postalCode': postalCode,
							'burglary': burglary ? 1 : 0,
							'burglaryAttempt': burglaryAttempt ? 1 : 0
						}), {
					method: 'GET'
				});

				const json = await res.json();
				if (json.status === 'ok')
					locations = json.locations;
				else
					locations = [];

				isLoading = false;

				// try to zoom in
				if (json.center) {
					map.setCenter(json.center);
					map.setZoom(zoom);
				} else {
					map.setCenter(defaultMapCenter);
					map.setZoom(defaultMapZoom);
				}

				// try to zoom in
				if (locations.length > 0) {
					showSearchBox = false;
					if (burglaryFormExists) {
						burglaryFormEmail.focus();
						burglaryFormEmail.blur();
						burglaryFormSubmit.click();
					}
				} else {
					if (json.center) {
						noResultsMessage = true;
						showSearchBox = false;
					} else
						notFoundMessage = true;
				}
			}
		}
    }

    async function loadDetailsInfoWindow() : Promise<void> {
        infoWindowLoading = true;
        const res = await fetch('/wp-json/burglary-check/v1/details?'+
            QueryString.stringify({
                'postalCode': infoWindowPostalCode ,
                'burglary': burglary?1:0,
                'burglaryAttempt': burglaryAttempt?1:0}), {
            method: 'GET'
        });

        const json = await res.json();
        if (json.status === 'ok')
            infoWindowLocations = json.locations;
        else
            infoWindowLocations = [];

        infoWindowLoading = false;
    }

    // draw markers when location changed
    $: if (google) {

        // hide old markers
        markers.map((marker) => {marker.setMap(null);});
        if (markerCluster)
            markerCluster.setMap(null);

        const markerIcon = {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 18,
            fillColor: "#FF9523",
            fillOpacity: 1,
            strokeColor: "#ffc384",
            strokeOpacity: 0.5,
            strokeWeight: 5,
            labelColor: "#FFFFFF"
        };

        // set new markers
        markers = allLocations.map((loc) => {
            const label = String(loc.quantity);
            const marker = new google.maps.Marker({
                'position': loc,
                'label': {text: label, color: '#FFFFFF'},
                'icon': markerIcon,
	            'optimized': true
            });

            // markers can only be keyboard focusable when they have click listeners
            // open info window when marker is clicked
            marker.addListener("click", () => {
                infoWindowPostalCode = loc.postalCode;
                loadDetailsInfoWindow();
                mapInfoWindow.open(map, marker);
            });

            return marker;
        });

        // Add a marker clusterer to manage the markers.
        const algorithm = new GridAlgorithm({maxDistance: 30});
        const renderer = {
            render: (cluster: Cluster) => {
                // calculate count
                let count = 0;
                cluster.markers.map((marker) => { count += parseInt(marker.label.text);});

                // draw marker
                return new google.maps.Marker({
                    'position': cluster.position,
                    'label': {text: String(count), color: '#FFFFFF'},
                    'icon': markerIcon,
                    'optimized': true,
                    // adjust zIndex to be above other markers
                    zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                });
            }
        };
        markerCluster = new MarkerClusterer( {markers, map ,algorithm, renderer});
    }

    function submitCheck(e) {
        if (e.charCode === 13)
            search();
    }

</script>

<div class="flex flex-col lg:flex-row-reverse xl:flex-row-reverse relative">
	<div class="basis-1/2 lg:px-7 mb-5" id="burglary-info">
		<h2>Bekijk het aantal inbraken</h2>

		<p>Voer uw gegevens in en bekijk het aantal inbraken in uw buurt. U ontvangt gratis tips om inbraak te voorkomen.</p>

		<div class="w-full" class:hidden={!showSearchBox}>
			{#if notFoundMessage}
				<div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-3" role="alert">
					Postcode niet gevonden
				</div>
			{/if}

			{#if noEmailMessage}
				<div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-3" role="alert">
					E-mail is onjuist
				</div>
			{/if}

			<div class="flex flex-col sm:flex-row gap-x-3 mb-3">
				<div class="basis-1/2 flex flex-col">
					<label for="bcPostalCode">Postcode</label>
					<input type="text" id="bcPostalCode" name="postalCode" placeholder="Postcode" bind:value={postalCode} class="rounded-full p-2 leading-[normal]" on:keypress={submitCheck} />
				</div>

				<div class="basis-1/2 flex flex-col">
					<label for="bcDistance">Afstand</label>
					<select name="distance" id="bcDistance" bind:value={distance} class="rounded-full p-2 text-base leading-[normal]">
						<option value="+1km">+1km</option>
						<option value="+2km">+2km</option>
						<option value="+5km">+5km</option>
						<option value="+10km">+10km</option>
						<option value="+25km">+25km</option>
					</select>
				</div>
			</div>

			{#if burglaryFormExists}
				<div class="flex flex-col sm:flex-row mb-3">
					<div class="basis-1/2 flex flex-col pr-[0.375rem]">
						<label for="bcEmail">E-mail</label>
						<input type="text" id="bcEmail" name="email" placeholder="E-mail" bind:value={email} class="rounded-full p-2 leading-[normal]" on:keypress={submitCheck}/>
					</div>
				</div>
			{/if}

			<div class="flex flex-col sm:flex-row gap-x-3 mb-3">
				<div class="basis-1/2">
					<input id="bcBurglary" type="checkbox" bind:checked={burglary} class="accent-checkbox-color" />
					<label for="bcBurglary">Woninginbraken</label>
				</div>

				<div class="basis-1/2">
					<input id="burglaryAttempt" type="checkbox" bind:checked={burglaryAttempt} class="accent-checkbox-color" />
					<label for="burglaryAttempt">Woninginbraak pogingen</label>
				</div>

			</div>

			<div class="flex flex-col sm:flex-row gap-x-3 gap-y-1 mb-3">
				<div class="basis-1/2 h-[40px] mb-2">
					<button class="bg-btn-primary-color hover:bg-btn-hover-color transition-colors duration-500 py-[10px] w-[280px] rounded-full border-none text-white text-base leading-[1rem] text-center" on:click={() => search()}>Zoeken</button>
				</div>
				<div class="basis-1/2 h-[40px]">
					<a class="btn-secondary bg-btn-secondary-color hover:bg-btn-hover-color transition-colors duration-500 py-[10px] w-[280px] rounded-full border-none text-white no-underline text-base leading-[1rem] inline-block text-center" href="#info">Meer lezen</a>
				</div>
			</div>
		</div>
		<div class="w-full" class:hidden={showSearchBox}>

			{#if noResultsMessage}
				<!--<div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-3" role="alert">
					Geen inbraken gevonden op deze postcode
				</div>-->
			{/if}

			<p>
				Postcode: `{postalCode}` {distance}<br>
				{#if burglary}+ Woninginbraken<br>{/if}
				{#if burglaryAttempt}+ Woninginbraak pogingen<br>{/if}
				<a class="cursor-pointer" href={null} on:click={() => showSearchBox = true}>Pas aan</a>
			</p>

			<h2 class="mt-8 lg:mt-32 xl:mt-32">Beter voorbereid zijn?</h2>
			<p>Vraag gratis en vrijblijvend professioneel advies aan om u en uw omgeving extra te beveiligen.</p>

			<div class="flex flex-col sm:flex-row gap-x-3 gap-y-1 mb-3">
				<div class="basis-1/2 h-[40px] mb-2">
					<a class="btn-secondary bg-btn-primary-color hover:bg-btn-hover-color transition-colors duration-500 py-[10px] w-[280px] rounded-full border-none text-white no-underline text-base leading-[1rem] inline-block text-center" href="#advies">Vraag advies</a>
				</div>
				<div class="basis-1/2 h-[40px]">
					<a class="btn-secondary bg-btn-secondary-color hover:bg-btn-hover-color transition-colors duration-500 py-[10px] w-[280px] rounded-full border-none text-white no-underline text-base leading-[1rem] inline-block text-center" href="#info">Meer lezen</a>
				</div>
			</div>
		</div>
	</div>

	<div class="basis-1/2 max-h-[300px]" id="burglary-map">
		<div bind:this={mapsDiv} class="w-full aspect-square flex min-h-[330px] max-h-[inherit]" class:hidden={view !== 'map'}>
			kaart
		</div>
		{#if view === 'list' && locations.length > 0}
			<div>
				<table class="border-none">
					<thead>
						<tr class="bg-[#F4F8FA]">
							<th class="text-left border-none">Postcodegebied</th>
							<th class="text-left border-none">Datum</th>
							<th class="text-left border-none">Type delict</th>
						</tr>
					</thead>
					<tbody>
					{#each locations as location}
						<tr class="even:bg-[#F4F8FA] odd:bg-white">
							<td class="text-left border-none">{location.postalCode}</td>
							<td class="text-left border-none">{location.date}</td>
							<td class="text-left border-none">{#if location.attempt}Poging tot woninginbraak{:else}Woninginbraak{/if}</td>
						</tr>
					{/each}
					</tbody>
				</table>
			</div>
		{/if}

		{#if locations.length > 0}
		<div class="mt-3">
			{#if view === 'list'}<a class="cursor-pointer" href={null} on:click={() => view = 'map'}>Toon op kaart</a>{/if}
			{#if view === 'map'}<a class="cursor-pointer" href={null} on:click={() => view = 'list'}>Toon als lijst</a>{/if}
		</div>
		{/if}

		<div class="hidden">
			<div id="infoWindowContent">
				<strong>Postcodegebied {infoWindowPostalCode}</strong><br>
				{#if infoWindowLoading}
					<div class="flex justify-center items-center h-24">
						<svg role="status" class="w-16 h-16 text-gray-200 animate-spin dark:text-gray-600 fill-btn-primary-color" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
							<path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
						</svg>
					</div>
				{:else}
					{#each infoWindowLocations as location}
						{#if location.attempt}Poging tot woninginbraak{:else}Woninginbraak{/if}<br>
						Datum: {location.date}<br>
						<hr>
					{/each}
                {/if}
			</div>
		</div>

	</div>

	{#if isLoading}
	<div class="absolute w-full h-full bg-black/50 flex justify-center items-center place-items-center">
		<div class=" h-24">
			<svg role="status" class="w-16 h-16 text-gray-200 animate-spin dark:text-gray-600 fill-btn-primary-color" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
				<path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
			</svg>
		</div>
	</div>
	{/if}
</div>
